import { useState } from "react";

import {
  BaseSelect,
  TableCellBadge,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import type { ProfileSettingsTableType } from "@/modules/mocksData";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import type { ColumnConfig } from "../type";

enum Column {
  LEVEL = "Level",
  NAME = "Name",
  ROLE = "Role",
}

const TABLE_HEADERS: TableHeaderType = Object.values(Column);
const COLUMN_WIDTHS = "w-[33%]";

interface RestaurantProfileSettingsTableProps {
  data: ProfileSettingsTableType[];
  roleOptions: { name: string; id: string }[];
  initialRoles?: Record<string, string>;
}

const RestaurantProfileSettingsTable = ({
  data,
  roleOptions,
  initialRoles,
}: RestaurantProfileSettingsTableProps) => {
  const { flags } = useVisibleFlags(TABLE_HEADERS);

  const [roles, setRoles] = useState<Record<string, string>>(
    () =>
      initialRoles ??
      Object.fromEntries(
        data.map(row => [row.id, row.role ?? roleOptions[0]?.id ?? ""])
      )
  );

  const handleRoleChange = (id: string, newValue: string) => {
    setRoles(prev => ({ ...prev, [id]: newValue }));
  };

  const columns: ColumnConfig<ProfileSettingsTableType>[] = [
    {
      key: "level",
      label: Column.LEVEL,
      visible: flags[Column.LEVEL],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText classes={COLUMN_WIDTHS} last={isLast}>
          {row.level}
        </TableCellText>
      ),
    },
    {
      key: "name",
      label: Column.NAME,
      visible: flags[Column.NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge classes={COLUMN_WIDTHS} last={isLast} stage="outline">
          {row.name}
        </TableCellBadge>
      ),
    },
    {
      key: "role",
      label: Column.ROLE,
      visible: flags[Column.ROLE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText classes={COLUMN_WIDTHS} last={isLast}>
          <BaseSelect
            value={roles[row.id]}
            onChange={value => handleRoleChange(row.id, value)}
            itemsList={roleOptions}
            triggerProps={{
              "data-testid": `profile-select-${row.id}`,
            }}
          />
        </TableCellText>
      ),
    },
  ];

  return <DynamicTable data={data} columns={columns} isPagination={false} />;
};

export default RestaurantProfileSettingsTable;
